'use client';

// ============================================================================
// SOFTWARE OVERVIEW TAB
// Agile context panel for software-development work items (epic, feature,
// story, bug, task, request, sprint, milestone, code_review, development_task).
//
// Layers:
//   - Type-aware hero (icon/colour per item type, story points, progress,
//     acceptance-criteria count)
//   - Delivery & planning fields writing custom_fields.roadmap
//     (horizon / quarter / theme / target_release — same contract the
//     roadmap service + roadmap-tools MCP use)
//   - Fetch-driven context cards from SoftwareAgileSections:
//     parent epic (child_of), sprint membership (work_item_sprints),
//     epic children rollup
//   - Bug report fields (severity column + repro custom fields)
//   - UnifiedOverviewTab below for the generic item sections
// ============================================================================

import React, { useMemo } from 'react';
import {
  Bug,
  BookOpen,
  Calendar,
  CheckSquare,
  ClipboardList,
  Flag,
  GitPullRequest,
  Inbox,
  Layers,
  Sparkles,
  Star,
  Wrench,
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import type { WorkItemOverviewInitialData } from '../work-items/detailBundleHydration';
import UnifiedOverviewTab from '../work-items/UnifiedOverviewTab';
import WorkItemAgentBadge from '../WorkItemAgentBadge';
import {
  EpicChildrenSection,
  ParentEpicSection,
  ROADMAP_CHILD_ITEM_TYPES,
  SprintMembershipSection,
} from './SoftwareAgileSections';

interface SoftwareOverviewTabProps {
  workItem: WorkItem;
  formData: Record<string, unknown>;
  onDataChange: (field: string, value: unknown) => void;
  isDark?: boolean;
  jobTypes?: Array<{ key: string; label: string; icon?: string; color?: string }>;
  initialData?: WorkItemOverviewInitialData | null;
  initialDataLoading?: boolean;
  workItemId?: string;
  workItemType?: string;
  onOpenItem?: (itemId: string) => void;
  onSubtasksCountChange?: (count: number) => void;
  onOpenSubtask?: (itemId: string) => void;
  moduleContext?: string | null;
}

const SOFTWARE_ITEM_TYPES = new Set([
  'bug', 'feature', 'story', 'epic', 'sprint', 'request', 'milestone',
  'task', 'code_review', 'development_task',
]);

interface TypeStyle {
  label: string;
  icon: React.ElementType;
  chip: string;
  iconClass: string;
}

const TYPE_STYLES: Record<string, { light: TypeStyle; dark: TypeStyle }> = {
  epic: {
    light: { label: 'Epic', icon: Layers, chip: 'bg-purple-50 text-purple-700', iconClass: 'text-purple-600' },
    dark: { label: 'Epic', icon: Layers, chip: 'bg-purple-900/30 text-purple-300', iconClass: 'text-purple-400' },
  },
  feature: {
    light: { label: 'Feature', icon: Sparkles, chip: 'bg-indigo-50 text-indigo-700', iconClass: 'text-indigo-600' },
    dark: { label: 'Feature', icon: Sparkles, chip: 'bg-indigo-900/30 text-indigo-300', iconClass: 'text-indigo-400' },
  },
  story: {
    light: { label: 'Story', icon: BookOpen, chip: 'bg-emerald-50 text-emerald-700', iconClass: 'text-emerald-600' },
    dark: { label: 'Story', icon: BookOpen, chip: 'bg-emerald-900/30 text-emerald-300', iconClass: 'text-emerald-400' },
  },
  bug: {
    light: { label: 'Bug', icon: Bug, chip: 'bg-red-50 text-red-700', iconClass: 'text-red-600' },
    dark: { label: 'Bug', icon: Bug, chip: 'bg-red-900/30 text-red-300', iconClass: 'text-red-400' },
  },
  task: {
    light: { label: 'Task', icon: CheckSquare, chip: 'bg-slate-100 text-slate-700', iconClass: 'text-slate-600' },
    dark: { label: 'Task', icon: CheckSquare, chip: 'bg-slate-800 text-slate-300', iconClass: 'text-slate-400' },
  },
  request: {
    light: { label: 'Request', icon: Inbox, chip: 'bg-amber-50 text-amber-700', iconClass: 'text-amber-600' },
    dark: { label: 'Request', icon: Inbox, chip: 'bg-amber-900/30 text-amber-300', iconClass: 'text-amber-400' },
  },
  sprint: {
    light: { label: 'Sprint', icon: Calendar, chip: 'bg-cyan-50 text-cyan-700', iconClass: 'text-cyan-600' },
    dark: { label: 'Sprint', icon: Calendar, chip: 'bg-cyan-900/30 text-cyan-300', iconClass: 'text-cyan-400' },
  },
  milestone: {
    light: { label: 'Milestone', icon: Flag, chip: 'bg-pink-50 text-pink-700', iconClass: 'text-pink-600' },
    dark: { label: 'Milestone', icon: Flag, chip: 'bg-pink-900/30 text-pink-300', iconClass: 'text-pink-400' },
  },
  code_review: {
    light: { label: 'Code review', icon: GitPullRequest, chip: 'bg-violet-50 text-violet-700', iconClass: 'text-violet-600' },
    dark: { label: 'Code review', icon: GitPullRequest, chip: 'bg-violet-900/30 text-violet-300', iconClass: 'text-violet-400' },
  },
  development_task: {
    light: { label: 'Dev task', icon: Wrench, chip: 'bg-teal-50 text-teal-700', iconClass: 'text-teal-600' },
    dark: { label: 'Dev task', icon: Wrench, chip: 'bg-teal-900/30 text-teal-300', iconClass: 'text-teal-400' },
  },
};

const HORIZON_OPTIONS: Array<{ value: 'now' | 'next' | 'later'; label: string; active: string; darkActive: string }> = [
  { value: 'now', label: 'Now', active: 'bg-emerald-600 text-white', darkActive: 'bg-emerald-600 text-white' },
  { value: 'next', label: 'Next', active: 'bg-blue-600 text-white', darkActive: 'bg-blue-600 text-white' },
  { value: 'later', label: 'Later', active: 'bg-violet-600 text-white', darkActive: 'bg-violet-600 text-white' },
];

const SEVERITY_OPTIONS = ['low', 'medium', 'high', 'critical'] as const;

function parseCustomFields(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function formatLabel(value: string) {
  return value.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function SoftwareOverviewTab({
  workItem,
  formData,
  onDataChange,
  isDark = false,
  jobTypes = [],
  initialData,
  initialDataLoading,
  workItemId,
  workItemType,
  onOpenItem,
  onOpenSubtask,
  onSubtasksCountChange,
  moduleContext,
}: SoftwareOverviewTabProps) {
  const effectiveType = workItemType || workItem.item_type;
  const customFields = useMemo(() => ({
    ...parseCustomFields(workItem.custom_fields),
    ...parseCustomFields(formData.custom_fields),
  }), [formData.custom_fields, workItem.custom_fields]);

  const roadmap = useMemo(() => parseCustomFields(customFields.roadmap), [customFields]);

  const storyPoints = Number(customFields.story_points || 0);
  const acceptanceCriteria = typeof customFields.acceptance_criteria === 'string' ? customFields.acceptance_criteria : '';
  const horizon = typeof roadmap.horizon === 'string' ? roadmap.horizon : null;
  const quarter = typeof roadmap.quarter === 'string' ? roadmap.quarter : '';
  const theme = typeof roadmap.theme === 'string' ? roadmap.theme : '';
  const targetRelease = typeof roadmap.target_release === 'string' ? roadmap.target_release : '';
  // Legacy free-text planning labels — superseded by the real epic link and
  // sprint membership, shown only as migration hints.
  const legacySprint = typeof customFields.sprint_name === 'string'
    ? customFields.sprint_name
    : typeof customFields.sprint === 'string' ? customFields.sprint : null;
  const legacyEpic = typeof customFields.epic_name === 'string'
    ? customFields.epic_name
    : typeof customFields.parent_epic === 'string' ? customFields.parent_epic : null;
  const contextLabel = moduleContext ? formatLabel(moduleContext) : 'Software Development';
  const criteriaLineCount = acceptanceCriteria.trim().length > 0
    ? acceptanceCriteria.trim().split(/\n+/).filter(Boolean).length
    : 0;
  const progressValue = Number.isFinite(Number(customFields.progress))
    ? Math.max(0, Math.min(100, Math.round(Number(customFields.progress))))
    : Math.max(0, Math.min(100, Math.round(Number(workItem.progress_percentage) || 0)));

  const severity = (formData.severity ?? workItem.severity ?? null) as string | null;
  const environment = typeof customFields.environment === 'string' ? customFields.environment : '';
  const reproSteps = typeof customFields.reproduction_steps === 'string' ? customFields.reproduction_steps : '';
  const expectedResult = typeof customFields.expected_result === 'string' ? customFields.expected_result : '';
  const actualResult = typeof customFields.actual_result === 'string' ? customFields.actual_result : '';

  const typeStyle = (TYPE_STYLES[effectiveType] || TYPE_STYLES.task)[isDark ? 'dark' : 'light'];
  const TypeIcon = typeStyle.icon;
  const isEpic = effectiveType === 'epic';
  const isBug = effectiveType === 'bug';
  const isChildType = ROADMAP_CHILD_ITEM_TYPES.has(effectiveType);
  const showSprintCard = !['sprint', 'milestone'].includes(effectiveType);
  const showAgileFields = SOFTWARE_ITEM_TYPES.has(effectiveType);
  const effectiveWorkItemId = workItemId || workItem.id;

  const updateCustomField = (key: string, value: unknown) => {
    const nextCustomFields = { ...customFields };
    if (value === '' || value === null || value === undefined) {
      delete nextCustomFields[key];
    } else {
      nextCustomFields[key] = value;
    }
    onDataChange('custom_fields', nextCustomFields);
  };

  /** Merge a single key into custom_fields.roadmap without clobbering
   *  siblings — the exact shape roadmapService's mergeRoadmapBag produces. */
  const updateRoadmapField = (key: 'horizon' | 'quarter' | 'theme' | 'target_release', value: string | null) => {
    const nextBag = { ...roadmap };
    if (value === null || value === '') {
      delete nextBag[key];
    } else {
      nextBag[key] = value;
    }
    const nextCustomFields = { ...customFields };
    if (Object.keys(nextBag).length > 0) {
      nextCustomFields.roadmap = nextBag;
    } else {
      delete nextCustomFields.roadmap;
    }
    onDataChange('custom_fields', nextCustomFields);
  };

  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm ${isDark
    ? 'border-gray-700 bg-gray-900 text-gray-100 placeholder-gray-500'
    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'}`;
  const cardCls = `rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`;
  const labelCls = `text-xs uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`;

  return (
    <div className="space-y-4">
      <WorkItemAgentBadge workItem={workItem} isDark={isDark} />

      {/* ── Agile hero ──────────────────────────────────────────────── */}
      <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch lg:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                <Sparkles className="h-3.5 w-3.5" />
                {contextLabel}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${typeStyle.chip}`}>
                <TypeIcon className={`h-3.5 w-3.5 ${typeStyle.iconClass}`} />
                {typeStyle.label}
              </span>
              {workItem.item_number && (
                <span className={`font-mono text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {workItem.item_number}
                </span>
              )}
            </div>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              Agile Overview
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {isEpic
                ? 'Delivery rollup, planning horizon and linked children for this epic.'
                : isBug
                  ? 'Defect context: severity, reproduction and delivery scheduling.'
                  : `Planning and delivery details for this ${typeStyle.label.toLowerCase()}.`}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 lg:min-w-[380px]">
            <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
              <div className={labelCls}>Story points</div>
              <div className="mt-2 flex items-center gap-1.5">
                <Star className={`h-3.5 w-3.5 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={Number.isFinite(storyPoints) ? storyPoints : 0}
                  onChange={(e) => updateCustomField('story_points', e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                  className={`w-full rounded-lg border px-2 py-1.5 text-sm ${isDark ? 'border-gray-700 bg-gray-900 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                  placeholder="0"
                />
              </div>
            </div>
            <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
              <div className={labelCls}>Progress</div>
              <div className="mt-2">
                <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{progressValue}%</div>
                <div className={`mt-1.5 h-1.5 w-full overflow-hidden rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${progressValue}%` }}
                  />
                </div>
              </div>
            </div>
            <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
              <div className={labelCls}>Acceptance</div>
              <div className={`mt-2 text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{criteriaLineCount}</div>
              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>criteria line{criteriaLineCount === 1 ? '' : 's'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Delivery context ────────────────────────────────────────── */}
      {isEpic && (
        <EpicChildrenSection
          workItemId={effectiveWorkItemId}
          isDark={isDark}
          onOpenSubtask={onOpenSubtask}
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {isChildType && (
          <ParentEpicSection
            workItemId={effectiveWorkItemId}
            itemType={effectiveType}
            isDark={isDark}
            onOpenItem={onOpenItem}
          />
        )}

        {showSprintCard && (
          <SprintMembershipSection
            workItemId={effectiveWorkItemId}
            itemType={effectiveType}
            isDark={isDark}
            storyPoints={Number.isFinite(storyPoints) ? storyPoints : 0}
          />
        )}

        {/* Roadmap planning — writes custom_fields.roadmap, the contract the
            roadmap service, report adapter and roadmap-tools all read. */}
        <div className={cardCls}>
          <div className={`flex items-center gap-2 ${labelCls}`}>
            <Flag className="h-3.5 w-3.5 text-pink-500" />
            <span className="font-semibold">Roadmap &amp; release</span>
          </div>
          <div className="mt-3 space-y-3">
            <div>
              <div className={`mb-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Horizon</div>
              <div className="flex gap-1.5">
                {HORIZON_OPTIONS.map((option) => {
                  const active = horizon === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => updateRoadmapField('horizon', active ? null : option.value)}
                      className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${active
                        ? (isDark ? option.darkActive : option.active)
                        : (isDark ? 'border border-gray-700 text-gray-400 hover:bg-gray-800' : 'border border-gray-300 text-gray-500 hover:bg-gray-100')}`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className={`mb-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Quarter</div>
                <input
                  type="text"
                  value={quarter}
                  onChange={(e) => updateRoadmapField('quarter', e.target.value || null)}
                  placeholder="Q1 2027"
                  className={inputCls}
                />
              </div>
              <div>
                <div className={`mb-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Theme</div>
                <input
                  type="text"
                  value={theme}
                  onChange={(e) => updateRoadmapField('theme', e.target.value || null)}
                  placeholder="e.g. Reliability"
                  className={inputCls}
                />
              </div>
              <div>
                <div className={`mb-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Target release</div>
                <input
                  type="text"
                  value={targetRelease}
                  onChange={(e) => updateRoadmapField('target_release', e.target.value || null)}
                  placeholder="e.g. v2.4"
                  className={inputCls}
                />
              </div>
            </div>
            {(legacySprint || legacyEpic) && (
              <div className={`border-t pt-2 text-xs ${isDark ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                Legacy labels{legacyEpic ? ` · epic: ${legacyEpic}` : ''}{legacySprint ? ` · sprint: ${legacySprint}` : ''}
                {' '}— superseded by the epic link and sprint membership above.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bug report ──────────────────────────────────────────────── */}
      {isBug && (
        <div className={cardCls}>
          <div className={`flex items-center gap-2 ${labelCls}`}>
            <Bug className="h-3.5 w-3.5 text-red-500" />
            <span className="font-semibold">Bug report</span>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div>
              <div className={`mb-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Severity</div>
              <div className="flex gap-1.5">
                {SEVERITY_OPTIONS.map((option) => {
                  const active = severity === option;
                  const tone = option === 'critical'
                    ? 'bg-red-600 text-white'
                    : option === 'high'
                      ? 'bg-orange-500 text-white'
                      : option === 'medium'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-500 text-white';
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => onDataChange('severity', active ? null : option)}
                      className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold capitalize transition-colors ${active
                        ? tone
                        : (isDark ? 'border border-gray-700 text-gray-400 hover:bg-gray-800' : 'border border-gray-300 text-gray-500 hover:bg-gray-100')}`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className={`mb-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Environment</div>
              <input
                type="text"
                value={environment}
                onChange={(e) => updateCustomField('environment', e.target.value)}
                placeholder="e.g. Android 15, Chrome 131, production"
                className={inputCls}
              />
            </div>
            <div className="lg:col-span-2">
              <div className={`mb-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Steps to reproduce</div>
              <textarea
                value={reproSteps}
                onChange={(e) => updateCustomField('reproduction_steps', e.target.value)}
                rows={3}
                placeholder={'1. …\n2. …\n3. …'}
                className={inputCls}
              />
            </div>
            <div>
              <div className={`mb-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Expected result</div>
              <textarea
                value={expectedResult}
                onChange={(e) => updateCustomField('expected_result', e.target.value)}
                rows={2}
                className={inputCls}
              />
            </div>
            <div>
              <div className={`mb-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Actual result</div>
              <textarea
                value={actualResult}
                onChange={(e) => updateCustomField('actual_result', e.target.value)}
                rows={2}
                className={inputCls}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Acceptance criteria ─────────────────────────────────────── */}
      {showAgileFields && (
        <div className={cardCls}>
          <div className={`flex items-center gap-2 ${labelCls}`}>
            <ClipboardList className="h-3.5 w-3.5 text-indigo-500" />
            <span className="font-semibold">Acceptance criteria</span>
            {criteriaLineCount > 0 && (
              <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>· {criteriaLineCount}</span>
            )}
          </div>
          <textarea
            value={acceptanceCriteria}
            onChange={(e) => updateCustomField('acceptance_criteria', e.target.value)}
            rows={5}
            className={`mt-3 ${inputCls}`}
            placeholder={'One criterion per line, e.g.\nGiven a concurrent edit, the API returns 409 rather than overwriting.\nThe actor, timestamp and payload hash are captured in the audit envelope.'}
          />
        </div>
      )}

      <UnifiedOverviewTab
        workItem={workItem}
        formData={formData}
        onDataChange={onDataChange}
        isDark={isDark}
        jobTypes={jobTypes}
        initialData={initialData}
        initialDataLoading={initialDataLoading}
        workItemId={workItemId}
        workItemType={workItemType}
        onOpenItem={onOpenItem}
        onOpenSubtask={onOpenSubtask}
        onSubtasksCountChange={onSubtasksCountChange}
      />
    </div>
  );
}
